import { CheckPicker } from "rsuite";
import { useDataset } from "../DataSet/DatasetProvider";
import { StandardFilterProps } from "../types";
import { ItemDataType } from "rsuite/lib/@types/common";
import { useEffect, useMemo, useRef } from "react";

export interface IDynamicCheckPickerProps extends StandardFilterProps {}

type CheckPickerItem = {
  id: string | number;
  text: string;
  searchString?: string;
};

const DynamicCheckPicker = (props: IDynamicCheckPickerProps) => {
  const { getFilterValue, setFilterValue, filtersDataSource } = useDataset();
  const rawValue = getFilterValue(props.filterDef.code);
  const k: string = props.filterDef.code;

  const data = useMemo(
    () => ((filtersDataSource as any)[k] as CheckPickerItem[]) ?? [],
    [filtersDataSource, k],
  );

  const normalizedValue = useMemo(
    () =>
      Array.isArray(rawValue)
        ? rawValue
        : rawValue !== undefined && rawValue !== null && rawValue !== ""
        ? [rawValue]
        : [],
    [rawValue],
  );

  useEffect(() => {
    if (normalizedValue.length === 0 || data.length === 0) {
      return;
    }

    const hasPlainIds = normalizedValue.some(
      (v) => typeof v !== "object" || v === null || !("text" in v),
    );

    if (!hasPlainIds) {
      return;
    }

    const dataMap = new Map<string | number, CheckPickerItem>();
    data.forEach((item) => {
      dataMap.set(item.id, item);
      dataMap.set(String(item.id), item);
      if (typeof item.id === "string" && !isNaN(Number(item.id))) {
        dataMap.set(Number(item.id), item);
      }
    });

    const convertedValues = normalizedValue
      .map((v) => {
        const id = typeof v === "object" && v !== null && "id" in v ? v.id : v;
        return dataMap.get(id);
      })
      .filter((item): item is CheckPickerItem => item !== undefined);

    if (convertedValues.length > 0) {
      setFilterValue(props.filterDef.code, convertedValues);
    }
  }, [data, normalizedValue, props.filterDef.code, setFilterValue]);

  const value = normalizedValue.map((v) =>
    typeof v === "object" && v !== null && "id" in v ? v.id : v,
  );

  const searchBy = (
    keyword: string,
    label: React.ReactNode,
    item: ItemDataType,
  ): boolean => {
    const text = typeof label === "string" ? label.toLowerCase() : "";
    const searchString = (
      ((item as unknown) as CheckPickerItem).searchString ?? ""
    )
      .toString()
      .toLowerCase();
    const k = keyword.toLowerCase();
    return text.includes(k) || searchString.includes(k);
  };
  const pickerRef = useRef<any>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const scrollParentRef = useRef<HTMLElement | null>(null);
  const scrollHandlerRef = useRef<(() => void) | null>(null);
  const isVertical = props.displayStyle === "vertical";

  const addScrollListener = () => {
    let el: HTMLElement | null = triggerRef.current?.parentElement ?? null;
    while (el && el !== document.body) {
      const { overflow, overflowY } = window.getComputedStyle(el);
      if (
        overflow === "auto" ||
        overflow === "scroll" ||
        overflowY === "auto" ||
        overflowY === "scroll"
      ) {
        break;
      }
      el = el.parentElement;
    }
    if (!el || el === document.body) return;

    const handler = () => pickerRef.current?.close();
    scrollHandlerRef.current = handler;
    scrollParentRef.current = el;
    el.addEventListener("scroll", handler);
  };

  const removeScrollListener = () => {
    if (scrollHandlerRef.current && scrollParentRef.current) {
      scrollParentRef.current.removeEventListener(
        "scroll",
        scrollHandlerRef.current,
      );
      scrollHandlerRef.current = null;
      scrollParentRef.current = null;
    }
  };

  const picker = (
    <CheckPicker
      ref={pickerRef}
      placement={isVertical ? "autoHorizontal" : "autoVertical"}
      preventOverflow
      block
      sticky
      searchable
      virtualized
      labelKey="text"
      valueKey="id"
      data={data}
      value={value}
      onOpen={() => {
        if (isVertical) addScrollListener();
        setTimeout(() => {
          const input = document.querySelector(
            ".rs-picker-search-bar-input",
          ) as HTMLInputElement | null;
          input?.focus();
        }, 50);
      }}
      onClose={() => {
        if (isVertical) removeScrollListener();
      }}
      onChange={(selectedIds) => {
        const dataMap = new Map(data.map((item) => [item.id, item]));
        const selectedObjects = (selectedIds ?? [])
          .map((id) => dataMap.get(id))
          .filter((item): item is CheckPickerItem => item !== undefined);
        setFilterValue(props.filterDef.code, selectedObjects);
      }}
      searchBy={searchBy}
      placeholder={props.placeholder}
      className={value.length > 0 ? "filled" : ""}
    />
  );

  if (isVertical) {
    return <div ref={triggerRef}>{picker}</div>;
  }

  return picker;
};

export default DynamicCheckPicker;
